import type { WorkspaceTab } from './components/TenantWorkspace.js';
import type { ReportSection } from './components/TenantReports.js';
import type { SettingsSection } from './components/TenantSettingsWorkspace.js';
import { AI_SETTINGS_NAVIGATION } from './ai-settings-navigation.js';

export interface WorkspaceLocation {
	claimConversation?: boolean;
	conversationId?: string;
	departmentId?: string;
	knowledgeCollectionSlug?: string;
	kind:
		| 'customer'
		| 'customers'
		| 'home'
		| 'owner-ai-agents'
		| 'owner-ai-usage'
		| 'owner-dashboard'
		| 'owner-message-enhance'
		| 'owner-plans'
		| 'owner-settings'
		| 'owner-ui-standards'
		| 'workspace';
	ownerTenantId?: string;
	reportSection?: ReportSection;
	settingsUserId?: string;
	settingsSection?: SettingsSection;
	tab?: WorkspaceTab;
	tenantId?: string;
	visitorId?: string;
}

const pathByTab: Record<WorkspaceTab, string> = {
	dashboard: '/dashboard',
	chats: '/chats',
	history: '/chat-history',
	reports: '/reports/agents',
	responses: '/responses',
	settings: '/settings',
	team: '/team',
	'visitor-history': '/visitor-history',
	visitors: '/visitors',
};

const legacySettingsSectionByPath = new Map<string, SettingsSection>([
	['/websites', 'integration'],
	['/departments', 'departments'],
	['/operations', 'operations'],
	['/settings/contact-form', 'offline-form'],
]);

const settingsSections = new Set<SettingsSection>([
	'user',
	'integration',
	'widget',
	'offline-form',
	'departments',
	'blocked-visitors',
	'webhooks',
	'operations',
	'updates',
	'api',
	...AI_SETTINGS_NAVIGATION.map((item) => item.section),
]);

const settingsPathBySection: Record<SettingsSection, string> = {
	user: '/settings',
	integration: '/settings/integration',
	widget: '/settings/widget',
	'offline-form': '/settings/offline-form',
	departments: '/settings/departments',
	'blocked-visitors': '/settings/blocked-visitors',
	webhooks: '/settings/webhooks',
	operations: '/settings/operations',
	updates: '/settings/updates',
	api: '/settings/api',
	ai: '/settings/ai',
	'ai-knowledge': '/settings/ai/knowledge',
	'ai-skills': '/settings/ai/skills',
	'ai-assignments': '/settings/ai/agent-actions',
	'ai-guardrails': '/settings/ai/guardrails',
	'ai-simulator': '/settings/ai/simulator',
	'ai-evaluations': '/settings/ai/evaluations',
	'ai-activity': '/settings/ai/activity',
};

const settingsSectionByPath = new Map(
	Object.entries(settingsPathBySection).map(([section, path]) => [
		path,
		section as SettingsSection,
	]),
);

const tabByPath = new Map(
	Object.entries(pathByTab).map(([tab, path]) => [path, tab as WorkspaceTab]),
);

const reportPathBySection: Record<ReportSection, string> = {
	agent: '/reports/agents',
	copilot: '/reports/ai-copilot',
	issues: '/reports/chat-issues',
	routing: '/reports/ai-routing',
};

const reportSectionByPath = new Map(
	Object.entries(reportPathBySection).map(([section, path]) => [path, section as ReportSection]),
);

export function membershipInvitationToken({
	search,
}: Pick<Location, 'search'>): string | undefined {
	return new URLSearchParams(search).get('membership') || undefined;
}

export function parseWorkspaceLocation({
	pathname,
	search,
}: Pick<Location, 'pathname' | 'search'>): WorkspaceLocation {
	const normalisedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
	if (normalisedPath === '/customers') return { kind: 'customers' };
	const customerMatch = normalisedPath.match(/^\/customers\/([^/]+)$/);
	if (customerMatch) {
		try {
			return {
				kind: 'customer',
				ownerTenantId: decodeURIComponent(customerMatch[1]!),
			};
		} catch {
			return { kind: 'home' };
		}
	}
	if (normalisedPath === '/owner/settings') return { kind: 'owner-settings' };
	if (normalisedPath === '/owner/dashboard') return { kind: 'owner-dashboard' };
	if (normalisedPath === '/owner/plans') return { kind: 'owner-plans' };
	if (normalisedPath === '/owner/message-enhance') return { kind: 'owner-message-enhance' };
	if (normalisedPath === '/owner/ai-agents') return { kind: 'owner-ai-agents' };
	if (normalisedPath === '/owner/ai-usage') return { kind: 'owner-ai-usage' };
	if (normalisedPath === '/owner/ui-standards') return { kind: 'owner-ui-standards' };
	const legacySettingsSection = legacySettingsSectionByPath.get(normalisedPath);
	const departmentMatch = normalisedPath.match(/^\/settings\/departments\/([^/]+)$/);
	const knowledgeMatch = normalisedPath.match(/^\/settings\/ai\/knowledge\/([^/]+)$/);
	let knowledgeCollectionSlug: string | undefined;
	let departmentId: string | undefined;
	try {
		departmentId = departmentMatch ? decodeURIComponent(departmentMatch[1]!) : undefined;
		knowledgeCollectionSlug = knowledgeMatch
			? decodeURIComponent(knowledgeMatch[1]!)
			: undefined;
	} catch {
		return { kind: 'home' };
	}
	const pathSettingsSection = departmentId
		? 'departments'
		: knowledgeCollectionSlug
			? 'ai-knowledge'
			: settingsSectionByPath.get(normalisedPath);
	const reportSection =
		normalisedPath === '/reports' ? 'agent' : reportSectionByPath.get(normalisedPath);
	const tab =
		legacySettingsSection || pathSettingsSection
			? 'settings'
			: reportSection
				? 'reports'
				: tabByPath.get(normalisedPath);
	if (!tab) return { kind: 'home' };
	const parameters = new URLSearchParams(search);
	const requestedSettingsSection =
		parameters.get('section') === 'contact-form' ? 'offline-form' : parameters.get('section');
	const validRequestedSettingsSection = settingsSections.has(
		requestedSettingsSection as SettingsSection,
	)
		? (requestedSettingsSection as SettingsSection)
		: undefined;
	const settingsSection =
		tab === 'settings'
			? normalisedPath === '/settings' && validRequestedSettingsSection
				? validRequestedSettingsSection
				: (pathSettingsSection ??
					legacySettingsSection ??
					validRequestedSettingsSection ??
					'user')
			: undefined;
	return {
		claimConversation: tab === 'chats' && parameters.get('claim') === '1' ? true : undefined,
		conversationId: tab === 'chats' ? (parameters.get('conversation') ?? undefined) : undefined,
		departmentId,
		knowledgeCollectionSlug,
		kind: 'workspace',
		reportSection: tab === 'reports' ? reportSection : undefined,
		settingsUserId: tab === 'settings' ? (parameters.get('user') ?? undefined) : undefined,
		settingsSection,
		tab,
		tenantId: parameters.get('tenant') ?? undefined,
		visitorId: tab === 'history' ? (parameters.get('visitor') ?? undefined) : undefined,
	};
}

export function workspaceHref({
	claimConversation,
	conversationId,
	departmentId,
	knowledgeCollectionSlug,
	reportSection,
	settingsUserId,
	settingsSection,
	tab,
	visitorId,
}: {
	claimConversation?: boolean;
	conversationId?: string;
	departmentId?: string;
	knowledgeCollectionSlug?: string;
	reportSection?: ReportSection;
	settingsUserId?: string;
	settingsSection?: SettingsSection;
	tab: WorkspaceTab;
	visitorId?: string;
}): string {
	const parameters = new URLSearchParams();
	if (tab === 'chats' && conversationId) parameters.set('conversation', conversationId);
	if (tab === 'chats' && conversationId && claimConversation) parameters.set('claim', '1');
	if (tab === 'settings' && settingsUserId) parameters.set('user', settingsUserId);
	if (tab === 'history' && visitorId) parameters.set('visitor', visitorId);
	const search = parameters.toString();
	const path =
		tab === 'settings'
			? settingsSection === 'departments' && departmentId
				? `${settingsPathBySection.departments}/${encodeURIComponent(departmentId)}`
				: settingsSection === 'ai-knowledge' && knowledgeCollectionSlug
					? `${settingsPathBySection['ai-knowledge']}/${encodeURIComponent(knowledgeCollectionSlug)}`
					: settingsPathBySection[settingsSection ?? 'user']
			: tab === 'reports'
				? reportPathBySection[reportSection ?? 'agent']
				: pathByTab[tab];
	return `${path}${search ? `?${search}` : ''}`;
}
